import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { useAuthStore } from '@/stores/auth';
import type { Organization } from '@/types/api';

export function useOrganizations() {
  const accessToken = useAuthStore((s) => s.accessToken);
  return useQuery({
    queryKey: ['organizations'],
    queryFn: async () => (await api.get<Organization[]>('/organizations')).data,
    enabled: !!accessToken,
  });
}

export function useOrganization(id: string | undefined) {
  return useQuery({
    enabled: !!id,
    queryKey: ['organizations', id],
    queryFn: async () => (await api.get<Organization>(`/organizations/${id}`)).data,
  });
}

export function useCreateOrganization() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: Partial<Organization> & { name: string; slug: string }) => {
      const { data } = await api.post<Organization>('/organizations', vars);
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['organizations'] }),
  });
}

export function useUpdateOrganization(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: Partial<Organization>) => {
      const { data } = await api.patch<Organization>(`/organizations/${id}`, vars);
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['organizations'] });
      qc.invalidateQueries({ queryKey: ['organizations', id] });
    },
  });
}

export function useOrgStorageUsage(id: string) {
  return useQuery({
    queryKey: ['org-storage', id],
    queryFn: async () =>
      (await api.get<{ used_bytes: number; quota_bytes: number | null }>(`/organizations/${id}/storage`)).data,
    enabled: !!id,
  });
}

/** Permanently deletes an organization and all its data. Sysadmin only.
 *  Backend requires `?confirm={org-name}` to guard against typos. */
export function useDeleteOrganization() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { id: string; confirm: string }) => {
      await api.delete(`/organizations/${vars.id}`, {
        params: { confirm: vars.confirm },
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['organizations'] });
    },
  });
}
